// src/pages/TrackCreation/Step1.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/ui/Button";
import { getProject, getProjects, getWaypointById } from "../../../../api";
import Dropdown from "../../../../components/common/Dropdown";
import RadioButton from "../../../../components/common/RadioButton";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import { useGlobalState } from "../../../../context/useGlobalState";
import { WaypointTextProps, RouteParams, DropdownOption } from "../../../../types/screensTypes";
import { idToLanguage } from "../../../../utils/idtolanguage";
import activeRadio from "../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../assets/images/radio_inactive.png";
import { Project } from "../../../../types/types";
import { useWaypointChangeDetection } from "../../../../hooks/useWaypointChangeDetection";
import stepStyles from "../createStep.module.css";
import wp from "./WaypointEdit.module.css";

const WaypointText: React.FC<WaypointTextProps> = ({ navigateToNext }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const { wptId, language } = useParams<RouteParams>();

    const {
        value: text,
        setValue: setText,
        saveIfChanged,
        setOriginalValue,
    } = useWaypointChangeDetection<string | null>({
        wptId: wptId || "",
        language,
        initialValue: null,
    });
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [isProject, setIsProject] = useState<boolean>(false);
    const [projectData, setProjectData] = useState<any>(null);
    const [projects, setProjects] = useState<DropdownOption[]>([]);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [selectedTextOption, setSelectedTextOption] = useState<string | null>(null);
    // Fetch details of a single project based on selected project ID.
    const fetchProject = async (id: string) => {
        if (!id) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await getProject({ token, projectId: id });
        if (response && response.id) {
            setProjectData(response);
        }
    };

    // Fetch list of projects for dropdown.
    const fetchProjects = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const response = await getProjects({ token });
            if (response) {
                const projectsDropdown = response.map((project: Project) => ({
                    label: project.name,
                    value: project.id,
                }));
                setProjects(projectsDropdown);
            }
        } catch (err) {
            console.error("Failed to fetch projects:", err);
        }
    };

    const handleTextSelect = (value: string) => {
        setSelectedTextOption(value);
        setText(value);
    };

    // Retrieve the waypoint text from the API.
    const getWaypoint = async (id: string) => {
        const token = localStorage.getItem("token");
        if (!token || !language) return;
        const waypointData = await getWaypointById({ token, id, language });
        let loadedText: string | null = null;

        if (waypointData) {
            const waypointArray = waypointData;
            if (
                waypointArray &&
                waypointArray.waypointTemporary &&
                waypointArray.waypointTemporary.TemporaryWaypointLocalized &&
                waypointArray.waypointTemporary.TemporaryWaypointLocalized.length > 0
            ) {
                const name = waypointArray.waypointTemporary.TemporaryWaypointLocalized[0].text;
                if (!name) {
                    const waypoint = waypointArray.waypoint;
                    if (waypoint && waypoint.waypointLocalized && waypoint.waypointLocalized.length > 0) {
                        loadedText = waypoint.waypointLocalized[0].fullText || null;
                    }
                } else {
                    loadedText = name;
                }
            } else {
                const waypoint = waypointArray.waypoint;
                if (waypoint && waypoint.waypointLocalized && waypoint.waypointLocalized.length > 0) {
                    loadedText = waypoint.waypointLocalized[0].fullText || null;
                }
            }
        }

        setText(loadedText);
        setOriginalValue(loadedText);
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchProject(selectedProject);
        }
    }, [selectedProject]);

    useEffect(() => {
        if (wptId) {
            getWaypoint(wptId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wptId, language]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setText(e.target.value);
    };

    const handleSaveChanges = async () => {
        if (text) {
            await saveIfChanged({ text });
        }
        if (!wptId || !language) return;
        navigateToNext(wptId, language);
    };

    const handleTextOptionChange = (value: string) => {
        setIsProject(value === "project");
    };

    const languagesAIJsonFormatted: DropdownOption[] =
        data?.languagesAI?.map((lang: any) => ({
            value: lang.code,
            label: lang.label,
        })) || [];

    const filteredAudioOptions =
        projectData?.audioAI
            ?.filter((audio: any) => audio.language === selectedContentLanguage)
            ?.sort((a: any, b: any) => a.position - b.position)
            .map((audio: any) => ({
                value: audio.text,
                label: audio.name,
            })) || [];

    return (
        <div className={`${stepStyles.editScreen} ${wp.screen}`}>
            <TitleSubtitle
                title={t("textEdition") + idToLanguage(language || "", data?.languagesAI, t)}
                subtitle={t("createOrEditYourTextManuallyOrBySelectingOption")}
                layout="edit"
            />
            <div className={wp.textArea}>
                <TextInput
                    placeholder={t("pleaseInsertYourText")}
                    value={text || ""}
                    onChange={handleTextChange}
                    size="big"
                    numberOfLines={8}
                />
            </div>
            <div className={wp.projectHeading}>
                <RadioButton
                    label={t("addFromYourProjects")}
                    checked={isProject}
                    onChange={() => handleTextOptionChange("project")}
                    activeImage={activeRadio}
                    inactiveImage={inactiveRadio}
                    center={true}
                />
            </div>
            {isProject && (
                <div className={wp.projectRow}>
                    <Dropdown
                        label=""
                        options={projects}
                        onChange={setSelectedProject}
                        placeholder={t("selectProject")}
                        loading={false}
                        error={false}
                        value={selectedProject}
                    />
                    <Dropdown
                        label=""
                        options={languagesAIJsonFormatted}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("selectALanguage")}
                        value={selectedContentLanguage}
                    />
                    {selectedProject && selectedContentLanguage && (
                        <Dropdown
                            label=""
                            options={filteredAudioOptions}
                            onChange={handleTextSelect}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectAText")}
                            value={selectedTextOption}
                        />
                    )}
                </div>
            )}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={handleSaveChanges}
                />
            </div>
        </div>
    );
};

export default WaypointText;
